<template>
  <div class="current-pay-plan-drawer">
    <div class="pppd__panel">
      <!-- Header -->
      <header class="pppd__header">
        <div class="pppd__header-bar">
          <div class="pppd__header-main">
            <div class="pppd__identity">
              <v-avatar size="32" class="pppd__avatar">
                <img
                  v-if="version.avatar_url"
                  :src="version.avatar_url"
                  :alt="version.employee"
                />
                <span v-else class="pppd__avatar-fallback">{{ employeeInitials }}</span>
              </v-avatar>
              <span class="pppd__employee-name">{{ version.employee }}</span>
              <span v-if="version.employee_title" class="pppd__employee-title">
                {{ version.employee_title }}
              </span>
            </div>
            <div class="pppd__header-meta">
              <VersionBadge :code="version.code" />
              <PayPlanLifecycleBadge status="In Force" />
              <PayPlanPeriodStateBadge state="current" filled />
              <span class="pppd__period-text">{{ headerPeriodText }}</span>
            </div>
          </div>
          <div class="pppd__header-actions">
            <ChatterIcon @click="$emit('open-chatter')" />
            <PayPlanRowMenu
              status="In Force"
              :terminable="terminable"
              :voidable="voidable"
              :void-blocked-by="voidBlockedBy"
              @action="handleMenuAction"
            />
            <button
              type="button"
              class="pppd__icon-btn"
              aria-label="Close"
              @click="$emit('close')"
            >
              <v-icon size="20">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div v-if="!loading" class="pppd__period-bar-wrap">
          <PeriodBar
            state="current"
            :label="periodLabel"
            :open-ended="isOpenEnded"
            :selectable="false"
          />
        </div>
        <div v-else class="pppd__period-bar-wrap">
          <div class="pppd__period-bar-skeleton"></div>
        </div>
      </header>

      <!-- Body -->
      <div v-if="loading" class="pppd__body">
        <div class="pppd__detail-skeleton">
          <div class="pppd__skeleton-line pppd__skeleton-line--short"></div>
          <div class="pppd__skeleton-line"></div>
          <div class="pppd__skeleton-line pppd__skeleton-line--medium"></div>
          <div class="pppd__skeleton-line"></div>
          <div class="pppd__skeleton-line pppd__skeleton-line--short"></div>
        </div>
      </div>
      <div v-else class="pppd__body">
        <!-- Info banner -->
        <div class="pppd__info-banner" role="status">
          <div class="pppd__info-banner-icon" aria-hidden="true">
            <v-icon size="20" color="#ffffff">mdi-information-outline</v-icon>
          </div>
          <p class="pppd__info-banner-text">This is the current pay plan version</p>
        </div>

        <!-- Primary action -->
        <div class="pppd__effective-row">
          <div class="pppd__primary-action">
            <StandardButton
              type="magenta"
              variant="primary"
              size="small"
              :disabled="proposeChangesDisabled"
              @click="$emit('propose-changes')"
            >
              Propose Changes
            </StandardButton>
          </div>
        </div>

        <!-- Future plan note -->
        <div v-if="futurePlanCode" class="pppd__future-plan-note">
          <p class="pppd__future-plan-text">
            A change is already scheduled ({{ futurePlanCode }}{{ futurePlanDate ? `, ${formatFairPayDate(futurePlanDate)}` : "" }}). Void it first to propose another.
          </p>
        </div>

        <!-- Country & currency -->
        <div class="pppd__field-row">
          <div class="pppd__field-col pppd__field-col--picker">
            <label class="pppd__field-label">
              Country
            </label>
            <div class="field-readonly">
              <div class="pppd__country-readonly">
                <CountryFlag v-if="version.country" :country-code="version.country" />
                <span>{{ version.country || '—' }}</span>
              </div>
              <v-icon size="20" class="pppd__lock-icon">mdi-lock</v-icon>
            </div>
          </div>
          <div class="pppd__field-col pppd__field-col--picker">
            <label class="pppd__field-label">
              Currency
            </label>
            <div class="field-readonly">
              <span>{{ version.currency || '—' }}</span>
              <v-icon size="20" class="pppd__lock-icon">mdi-lock</v-icon>
            </div>
          </div>
        </div>

        <!-- Salary, FTE, pay basis -->
        <div class="pppd__field-row pppd__field-row--salary">
          <div class="pppd__field-col pppd__field-col--salary">
            <label class="pppd__field-label">
              Base Salary
            </label>
            <div class="field-readonly">
              <span>{{ formatAmountDisplay }}</span>
              <v-icon size="20" class="pppd__lock-icon">mdi-lock</v-icon>
            </div>
          </div>
          <div class="pppd__field-col">
            <label class="pppd__field-label">
              FTE
              <EXTooltip placement="top" title="From primary assignment">
                <v-icon small class="pppd__help-icon">mdi-help-circle-outline</v-icon>
              </EXTooltip>
            </label>
            <div class="field-readonly pppd__fte-readonly">
              <span>{{ fteDisplayLabel }}</span>
              <v-icon size="20" class="pppd__lock-icon">mdi-lock</v-icon>
            </div>
          </div>
          <div class="pppd__field-col pppd__field-col--basis">
            <label class="pppd__field-label">
              Pay Basis
            </label>
            <div class="field-readonly">
              <span>{{ payBasisDisplay }}</span>
              <v-icon size="20" class="pppd__lock-icon">mdi-lock</v-icon>
            </div>
          </div>
        </div>

        <!-- Fair Pay status line -->
        <div v-if="readinessEnabled && fairPayLineText" class="pppd__fair-pay-status">
          <span class="pppd__fair-pay-status-text">{{ fairPayLineText }}</span>
        </div>
        <div
          v-else-if="readinessEnabled && fairPayAtEffectiveDate === null"
          class="pppd__fair-pay-status"
        >
          <span class="pppd__fair-pay-status-text">No pay range at effective date — Fair Pay is not compared</span>
        </div>

        <!-- Rule A-40: Readiness off shows the range penetration instead of Fair Pay -->
        <PayRangePenetrationBar
          v-if="showPenetration"
          class="pppd__penetration"
          :range-penetration="rangePenetration"
          :currency-symbol="currencySymbol"
        />

        <!-- Fair Pay calculator -->
        <FairPayCalculatorAtEffectiveDateCard
          v-else-if="readinessEnabled && fairPayAtEffectiveDate !== null"
          class="pppd__calculator"
          calculator="read-only"
          :as-of-date="formatFairPayDate(version.effective_from)"
          :position="calculatorPosition"
          :pay-range-version="calculatorPayRangeVersion"
          :pay-range-adjusted="calculatorPayRangeAdjusted"
          :fte-adjustment-applied="fteAdjustmentApplied"
          :current-readiness="fairPayAtEffectiveDate.readiness"
          :fair-at-current="fairAtCurrent"
          :readiness-enabled="readinessEnabled"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import CountryFlag from "@components/CountryFlag/CountryFlag.vue";
import PeriodBar from "@components/PeriodBar/PeriodBar.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import VersionBadge from "@components/VersionBadge/VersionBadge.vue";
import PayPlanLifecycleBadge from "@components/PayPlanLifecycleBadge/PayPlanLifecycleBadge.vue";
import PayPlanPeriodStateBadge from "@components/PayPlanPeriodStateBadge/PayPlanPeriodStateBadge.vue";
import PayPlanRowMenu from "@components/PayPlanRowMenu/PayPlanRowMenu.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import PayRangePenetrationBar from "@components/PayRangePenetrationBar/PayRangePenetrationBar.vue";
import FairPayCalculatorAtEffectiveDateCard from "@components/FairPayCalculatorAtEffectiveDateCard/FairPayCalculatorAtEffectiveDateCard.vue";
import { formatAssignmentFte } from "@components/EmployeeToPrimaryAssignmentBar/employeeToPrimaryAssignmentBarUtils";
import { formatFairPayDate } from "@components/utils/fairPayFormat";
import { getCurrencySymbol } from "@components/FairPayStatusShort/formatFairPayStatusAmount";

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  version: {
    type: Object,
    required: true,
    // code, status, effective_from, effective_to, country, currency, amount, pay_basis, fte, employee, employee_title, avatar_url
  },
  fairPayAtEffectiveDate: {
    type: Object,
    default: null,
    // status (fairly-paid | below | above), readiness, fair_increase, position, pay_range_version, adjusted_band
  },
  readinessEnabled: {
    type: Boolean,
    default: true,
  },
  /** Rule A-40: { min, mid, max, position_pct } shown when Readiness is off. */
  rangePenetration: {
    type: Object,
    default: null,
  },
  futurePlanCode: {
    type: String,
    default: null,
  },
  futurePlanDate: {
    type: String,
    default: null,
  },
  voidable: {
    type: Boolean,
    default: false,
  },
  voidBlockedBy: {
    type: String,
    default: "",
  },
  terminable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "propose-changes",
  "terminate",
  "void",
  "open-chatter",
  "close",
]);

const employeeInitials = computed(() => {
  const parts = String(props.version.employee || "")
    .trim()
    .split(/\s+/);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
});

const headerPeriodText = computed(() => {
  const from = formatFairPayDate(props.version.effective_from);
  const to = props.version.effective_to ? formatFairPayDate(props.version.effective_to) : "—";
  return `Effective from ${from} · Effective to ${to}`;
});

const periodLabel = computed(() => {
  const amount = formatAmountNumber(props.version.amount);
  const basis = formatPayBasisShort(props.version.pay_basis);
  return `${amount} ${props.version.currency}/${basis}`;
});

const isOpenEnded = computed(
  () => !props.version.effective_to || props.version.effective_to === null
);

const formatAmountDisplay = computed(() => {
  const amount = formatAmountNumber(props.version.amount);
  const basis = formatPayBasisShort(props.version.pay_basis);
  return `${amount} ${props.version.currency}/${basis}`;
});

const payBasisDisplay = computed(() => {
  const basis = formatPayBasisShort(props.version.pay_basis);
  return `Per ${basis}`;
});

const fteDisplayLabel = computed(() => {
  const fte = props.version.fte ?? 1;
  const n = Number(fte);
  if (Number.isFinite(n) && n === 1) {
    return "1 - FTE";
  }
  return `${formatAssignmentFte(fte)}`.replace(" FTE", " - FTE");
});

const showPenetration = computed(() => !props.readinessEnabled && props.rangePenetration != null);
const currencySymbol = computed(() => getCurrencySymbol(props.version.currency || "USD"));

const proposeChangesDisabled = computed(() => props.futurePlanCode !== null);

const fteAdjustmentApplied = computed(() => {
  if (!props.fairPayAtEffectiveDate) return false;
  const fte = props.version.fte ?? 1;
  return Number(fte) < 1;
});

const fairPayLineText = computed(() => {
  if (!props.fairPayAtEffectiveDate) return "";

  const { status, readiness, fair_increase } = props.fairPayAtEffectiveDate;
  const date = formatFairPayDate(props.version.effective_from);
  const fteSuffix = fteAdjustmentApplied.value ? ` (${props.version.fte} FTE range)` : "";

  if (status === "fairly-paid") {
    return `Fairly paid at ${readiness}% readiness on ${date}${fteSuffix}`;
  } else if (status === "below") {
    const increase = formatFairPayAmount(fair_increase);
    return `Below fair at ${readiness}% readiness on ${date} · Fair Increase ${increase}${fteSuffix}`;
  } else if (status === "above") {
    const above = formatFairPayAmount(fair_increase);
    return `Above fair at ${readiness}% readiness on ${date} · Above Fair Pay ${above}${fteSuffix}`;
  }

  return "";
});

const calculatorPosition = computed(() => {
  if (props.fairPayAtEffectiveDate?.position) {
    return props.fairPayAtEffectiveDate.position;
  }
  return {
    code: props.version.position_code || "",
    name: props.version.position_name || "",
    fte: props.version.fte ?? 1,
  };
});

function buildAnchors(range) {
  if (range?.anchors) return range.anchors;
  const min = Number(range?.min) || 0;
  const max = Number(range?.max) || min;
  const span = max - min;
  return {
    0: min,
    50: min + span * 0.33,
    100: min + span * 0.67,
    150: max,
  };
}

const calculatorPayRangeVersion = computed(() => {
  if (props.fairPayAtEffectiveDate?.pay_range_version) {
    return props.fairPayAtEffectiveDate.pay_range_version;
  }
  return {
    version: 1,
    asOfDate: formatFairPayDate(props.version.effective_from),
    code: "",
    jobCategory: "",
    level: "",
    currency: props.version.currency || "USD",
    payBasis: formatPayBasisShort(props.version.pay_basis),
    location: "",
    countryFlag: "",
    anchors: buildAnchors(props.fairPayAtEffectiveDate || {}),
  };
});

const calculatorPayRangeAdjusted = computed(() => {
  if (!fteAdjustmentApplied.value || !props.fairPayAtEffectiveDate?.adjusted_band) {
    return null;
  }
  if (props.fairPayAtEffectiveDate.adjusted_band.anchors) {
    return props.fairPayAtEffectiveDate.adjusted_band;
  }
  return {
    anchors: buildAnchors(props.fairPayAtEffectiveDate.adjusted_band),
  };
});

const fairAtCurrent = computed(() => {
  if (!props.readinessEnabled || !props.fairPayAtEffectiveDate) return null;
  if (props.fairPayAtEffectiveDate.fair_at_current) {
    return props.fairPayAtEffectiveDate.fair_at_current;
  }
  return {
    amount: Number(String(props.version.amount).replace(/,/g, "")) || 0,
    currency: props.version.currency || "USD",
    basis: formatPayBasisCode(props.version.pay_basis),
    variance: "±2%",
  };
});

function formatAmountNumber(value) {
  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(value || "");
  return new Intl.NumberFormat("en-US").format(n);
}

function formatFairPayAmount(value) {
  if (!value) return "";
  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(value || "");
  const sign = n >= 0 ? "+" : "";
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: props.version.currency || "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(n));
  return `${sign}${formatted}`;
}

function formatPayBasisShort(basis) {
  const map = {
    Year: "Year",
    Month: "Month",
    Hour: "Hour",
    per_year: "Year",
    per_month: "Month",
    per_hour: "Hour",
  };
  return map[basis] || "Year";
}

function formatPayBasisCode(basis) {
  const map = {
    Year: "yr",
    Month: "mo",
    Hour: "hr",
    per_year: "yr",
    per_month: "mo",
    per_hour: "hr",
  };
  return map[basis] || "yr";
}

function handleMenuAction(action) {
  if (action === "terminate") {
    emit("terminate");
  } else if (action === "void") {
    emit("void");
  }
}
</script>

<style scoped>
.current-pay-plan-drawer {
  width: 100%;
  height: 100%;
}

.pppd__panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  background: #ffffff;
  border: 1px solid #eaecf0;
}

.pppd__header {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 0 24px;
}

.pppd__header-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-top: 24px;
}

.pppd__header-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.pppd__identity {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pppd__avatar {
  flex-shrink: 0;
}

.pppd__avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f9fafb;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.pppd__employee-name {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.pppd__employee-title {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.pppd__header-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.pppd__period-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.pppd__header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pppd__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #667085;
}

.pppd__icon-btn:hover {
  color: #344054;
}

.pppd__period-bar-wrap {
  width: 100%;
}

.pppd__period-bar-skeleton {
  width: 100%;
  height: 32px;
  background: #eaecf0;
  border-radius: 6px;
}

.pppd__body {
  display: flex;
  flex-direction: column;
  gap: 17px;
  background: #ffffff;
  border-radius: 12px;
  padding-bottom: 24px;
}

.pppd__detail-skeleton {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px;
}

.pppd__skeleton-line {
  height: 16px;
  background: #eaecf0;
  border-radius: 4px;
  width: 100%;
}

.pppd__skeleton-line--short {
  width: 40%;
}

.pppd__skeleton-line--medium {
  width: 70%;
}

.pppd__info-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 24px;
  background: #7f56d9;
  border-radius: 8px;
  margin: 0 24px;
}

.pppd__info-banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.pppd__info-banner-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #ffffff;
}

.pppd__effective-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 0 24px;
}

.pppd__primary-action {
  flex-shrink: 0;
}

.pppd__future-plan-note {
  padding: 0 24px;
}

.pppd__future-plan-text {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #475467;
}

.pppd__field-row {
  display: flex;
  gap: 17px;
  padding: 0 24px;
}

.pppd__field-row--salary {
  display: flex;
  gap: 17px;
}

.pppd__field-col {
  flex: 1;
  min-width: 0;
}

.pppd__field-col--salary {
  flex: 1 1 312px;
}

.pppd__field-col--picker {
  flex: 1;
  min-width: 0;
}

.pppd__field-col--basis {
  flex: 1;
  min-width: 0;
}

.pppd__field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.pppd__help-icon {
  color: #475467;
}

.field-readonly,
.pppd__fte-readonly {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  min-height: 44px;
  background: #e0e0e0;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #4d4d4d;
}

.pppd__country-readonly {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pppd__lock-icon {
  color: #667085;
  flex-shrink: 0;
}

.pppd__fair-pay-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 0 24px;
}

.pppd__fair-pay-status-text {
  font-size: 12px;
  font-weight: 500;
  color: #475467;
}

.pppd__calculator,
.pppd__penetration {
  margin: 0 24px;
}
</style>
